// core/action/authActions.js
import { authAPI } from './api';
import { toast } from 'react-toastify';

export const authActions = {
    // Register new user
    register: async (userData) => {
        try {
            // For FormData, we need to extract name for validation
            let name = '';
            
            if (userData instanceof FormData) {
                name = userData.get('name') || '';
            } else {
                name = userData.name || '';
            }
            
            // Validate required fields
            if (!name) {
                return { success: false, message: 'Name is required' };
            }
            
            const response = await authAPI.register(userData);
            
            // Check response structure - directly check response.success
            if (response && response.success === true) {
                toast.success(response.message || 'Registration successful');
                return {
                    success: true,
                    data: response.data || null,
                    message: response.message || 'Registration successful'
                };
            } else {
                toast.error(response?.message || 'Registration failed');
                return {
                    success: false,
                    message: response?.message || 'Registration failed'
                };
            }
        } catch (error) {
            console.error('Registration error:', error);
            toast.error(error.message || 'Registration failed');
            return {
                success: false,
                message: error.message || 'Registration failed'
            };
        }
    },

   // core/action/authActions.js

login: async (credentials) => {
    try {
        if (!credentials.email || !credentials.password) {
            return { success: false, message: 'Email/Employee ID and password are required' };
        }
        
        const response = await authAPI.login(credentials);
        
        // Check response structure
        if (response && response.success === true && response.data) {
            // Store token and user data
            if (response.data.token) {
                localStorage.setItem('auth_token', response.data.token);
            }
            if (response.data.user) {
                localStorage.setItem('user', JSON.stringify(response.data.user));
            }
            
            return {
                success: true,
                data: response.data,
                message: response.message || 'Login successful'
            };
        } else {
            return {
                success: false,
                message: response?.message || 'Invalid credentials'
            };
        }
    } catch (error) {
        console.error('Login error:', error);
        return {
            success: false,
            message: error.message || 'Login failed. Please try again.'
        };
    }
},
    // Forgot password
    forgotPassword: async (email) => {
        try {
            if (!email) {
                return { success: false, message: 'Email is required' };
            }
            
            const response = await authAPI.forgotPassword({ email });
            
            if (response && response.success === true) {
                toast.success(response.message || 'Password reset link sent');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to send reset link');
                return {
                    success: false,
                    message: response?.message || 'Failed to send reset link'
                };
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            toast.error(error.message || 'Failed to send reset link');
            return {
                success: false,
                message: error.message || 'Failed to send reset link'
            };
        }
    },

    // Reset password
    resetPassword: async (token, newPassword) => {
        try {
            if (!token || !newPassword) {
                return { success: false, message: 'Token and new password are required' };
            }
            
            if (newPassword.length < 6) {
                return { success: false, message: 'Password must be at least 6 characters' };
            }
            
            const response = await authAPI.resetPassword({ token, password: newPassword });
            
            if (response && response.success === true) {
                toast.success(response.message || 'Password reset successful');
                return {
                    success: true,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to reset password');
                return {
                    success: false,
                    message: response?.message || 'Failed to reset password'
                };
            }
        } catch (error) {
            console.error('Reset password error:', error);
            toast.error(error.message || 'Failed to reset password');
            return {
                success: false,
                message: error.message || 'Failed to reset password'
            };
        }
    },

    // Change password (when logged in)
    changePassword: async (currentPassword, newPassword) => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                return { success: false, message: 'Not authenticated' };
            }
            
            if (!currentPassword || !newPassword) {
                return { success: false, message: 'Current and new password are required' };
            }
            
            if (newPassword.length < 6) {
                return { success: false, message: 'New password must be at least 6 characters' };
            }
            
            const response = await authAPI.changePassword({
                current_password: currentPassword,
                new_password: newPassword
            }, token);
            if (response && response.success === true) {
                toast.success(response.message || 'Password changed successfully');
                return {
                    success: true,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to change password');
                return {
                    success: false,
                    message: response?.message || 'Failed to change password'
                };
            }
        } catch (error) {
            console.error('Change password error:', error);
            toast.error(error.message || 'Failed to change password');
            return {
                success: false,
                message: error.message || 'Failed to change password'
            };
        }
    },

    // Get current user profile
   // Get current user profile
getProfile: async () => {
    try {
        const token = localStorage.getItem('auth_token');
        
        if (!token) {
            // Try to get from localStorage as fallback
            const user = authActions.getCurrentUser();
            if (user) {
                return {
                    success: true,
                    data: user,
                    fromCache: true
                };
            }
            return { success: false, message: 'Not authenticated' };
        }
        
        // Try to get from API
        try {
            const response = await authAPI.getProfile(token);
            
            if (response && response.success === true) {
                // Update localStorage with fresh data
                localStorage.setItem('user', JSON.stringify(response.data));
                return {
                    success: true,
                    data: response.data
                };
            }
        } catch (apiError) {
            console.warn('API getProfile failed, using cached data:', apiError);
        }
        
        // If API fails, use cached data
        const user = authActions.getCurrentUser();
        if (user) {
            return {
                success: true,
                data: user,
                fromCache: true
            };
        }
        
        return {
            success: false,
            message: 'Failed to get profile'
        };
    } catch (error) {
        console.error('Get profile error:', error);
        
        // Fallback to cached user
        const user = authActions.getCurrentUser();
        if (user) {
            return {
                success: true,
                data: user,
                fromCache: true
            };
        }
        
        return {
            success: false,
            message: error.message || 'Failed to get profile'
        };
    }
},

    // Update profile
    updateProfile: async (profileData) => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                return { success: false, message: 'Not authenticated' };
            }
            
            const response = await authAPI.updateProfile(profileData, token);
            
            if (response && response.success === true) {
                // Update stored user data
                const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                const updatedUser = { ...currentUser, ...response.data };
                localStorage.setItem('user', JSON.stringify(updatedUser));
                
                toast.success(response.message || 'Profile updated successfully');
                return {
                    success: true,
                    data: response.data,
                    message: response.message
                };
            } else {
                toast.error(response?.message || 'Failed to update profile');
                return {
                    success: false,
                    message: response?.message || 'Failed to update profile'
                };
            }
        } catch (error) {
            console.error('Update profile error:', error);
            toast.error(error.message || 'Failed to update profile');
            return {
                success: false,
                message: error.message || 'Failed to update profile'
            };
        }
    },

    // Verify token
    verifyToken: async () => {
        try {
            const token = localStorage.getItem('auth_token');
            
            if (!token) {
                return { success: false };
            }
            
            return {
                success: true,
                data: authActions.getCurrentUser()
            };
        } catch (error) {
            console.error('Token verification error:', error);
            return { success: false };
        }
    },

    // Logout
    logout: () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        toast.success('Logged out successfully');
        return { success: true };
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        return !!localStorage.getItem('auth_token');
    },

    // Get current user
    getCurrentUser: () => {
        try {
            const userStr = localStorage.getItem('user');
            return userStr ? JSON.parse(userStr) : null;
        } catch {
            return null;
        }
    },

    // Get auth token
    getToken: () => {
        return localStorage.getItem('auth_token');
    },

    // Check if user has permission
    hasPermission: (permissionSlug) => {
        const user = authActions.getCurrentUser();
        if (!user || !user.permissions) return false;
        
        if (user.role_slug === 'super-admin') return true;
        
        return user.permissions.includes(permissionSlug);
    },

    // Check if user has any of the given permissions
    hasAnyPermission: (permissions) => {
        const user = authActions.getCurrentUser();
        if (!user || !user.permissions) return false;
        
        if (user.role_slug === 'super-admin') return true;
        
        return permissions.some(p => user.permissions.includes(p));
    },

    // Check if user has all of the given permissions
    hasAllPermissions: (permissions) => {
        const user = authActions.getCurrentUser();
        if (!user || !user.permissions) return false;
        
        if (user.role_slug === 'super-admin') return true;
        
        return permissions.every(p => user.permissions.includes(p));
    },

    // Get user role
    getUserRole: () => {
        const user = authActions.getCurrentUser();
        return user?.role_slug || null;
    },

    // Check if user is admin
    isAdmin: () => {
        const user = authActions.getCurrentUser();
        if (!user) return false;
        return user.role_slug === 'super-admin' || user.role_slug === 'admin';
    },

    // Get user initials
    getUserInitials: () => {
        const user = authActions.getCurrentUser();
        if (!user?.name) return 'U';
        return user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    },

    // Format user name
    formatUserName: () => {
        const user = authActions.getCurrentUser();
        return user?.name || 'Guest';
    },

    // Get user role display name
    getUserRoleName: () => {
        const user = authActions.getCurrentUser();
        return user?.role_name || 'User';
    }
};

export default authActions;
